import moment from 'moment';
import { useState } from 'react';
import { CustomerState } from '../../../../../types/CustomerDto';
import { ADMIN_BILLING_CHECKOUT } from '../../../../routes';
import LinkButton from '../../../components/LinkButton';
import Modal from '../../../components/Modal';
import { ModalTitle } from '../../../components/Modal/presenter';
import { useAppContext } from '../../../hooks/useAppContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { interpolateAppUrl } from '../../../services/Url';
import HeaderSpark from '../../HeaderSpark';
import { sumBy } from '../services/plan';
import useCustomer from '../useCustomer';

interface Props {
    onDismiss?: () => void;
    onClose?: () => void;
}

export default function PromptToCheckout(props: Props) {
    const { user } = useAppContext();
    if (!user.secureLogin) {
        return null;
    }
    return <PromptToCheckoutInner {...props} />;
}

function PromptToCheckoutInner({ onDismiss, onClose }: Props) {
    const customer = useCustomer();
    const [dismissedAt, setDismissedAt] = useLocalStorage<number | null>('dismissedTrialCheckoutReminder', null);
    const [hidden, setHidden] = useState<boolean>(false);

    if (!customer) return null;
    if (hidden) return null;
    if (sumBy(customer.priceInfo, 'monthlyWithNonProfitAndDiscount') === 0) {
        // once this spark is hidden because the price === 0, never show it again before the page is reloaded.
        // we persist this decision in the state to avoid that the modal pops up when you
        // enter a license count in the checkout screen
        setHidden(true);
        return null;
    }

    if (customer.state === CustomerState.FUTURE) {
        if (deadlineIsNear(customer.subscription.startDate, 7)) {
            return <PromptModal onClose={onClose} onCheckout={onClose} />;
        } else {
            return <PromptBanner />;
        }
    } else if (customer.state === CustomerState.TRIAL) {
        if (deadlineIsNear(customer.subscription.trialEnd, 7)) {
            if (dismissedAt && moment(dismissedAt).isAfter(moment().subtract(1, 'day'))) {
                return null;
            }
            const handleDismiss = () => {
                if (onDismiss) {
                    setDismissedAt(Date.now());
                    onDismiss();
                }
            };
            return <PromptBanner onClickX={handleDismiss} onClickLink={onClose} />;
        }
    }

    return null;
}

function PromptModal(props: { onClose?(): void; onCheckout?(): void }) {
    return (
        <Modal isOpen onClose={props.onClose}>
            <ModalTitle>Please complete your checkout</ModalTitle>
            <p>
                <Text onClick={props.onCheckout} />
            </p>
            <LinkButton kind="PRIMARY" href={interpolateAppUrl(ADMIN_BILLING_CHECKOUT, {})} onClick={props.onCheckout}>
                Check out now
            </LinkButton>
        </Modal>
    );
}

function PromptBanner(props: { onClickX?(): void; onClickLink?(): void }) {
    return (
        <HeaderSpark onDone={props.onClickX}>
            <strong>Please complete your checkout:</strong> <Text onClick={props.onClickLink} />
        </HeaderSpark>
    );
}

function Text(props: { onClick?(): void }) {
    return (
        <>
            Your subscription is starting soon, but we do not have your payment details on file. Please{' '}
            <a onClick={props.onClick} href={interpolateAppUrl(ADMIN_BILLING_CHECKOUT, {})}>
                visit the check-out page
            </a>{' '}
            and enter your payment details to activate your subscription.
        </>
    );
}

function deadlineIsNear(deadline: moment.MomentInput, days: number): boolean {
    const near = moment().add(days, 'days');
    return moment(deadline).isSameOrBefore(near);
}
